import React, { useState, useRef } from 'react';
import { Card, Steps, Button, Space, message, Form } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { gqlClient } from '../../../helpers/gqlProvider';
import { SUPPLIER_PORTAL_PUBLISH_SOURCING_MUTATION } from '../../sourcing/queries';
import { ConfigureTab } from './ConfigureTab';
import { InviteTab } from './InviteTab';
import { ReviewTab } from './ReviewTab';

const { Step } = Steps;

interface PublishSourcingFormData {
  // Configure tab data
  templateId: string;
  sourcingType: 'rfq' | 'rfp';
  biddingType: 'ncb' | 'icb';
  fields: Array<{
    name: string;
    label: string;
    description: string;
    type: string;
    isRequired: boolean;
  }>;
  fileRequirements: Array<{
    name: string;
    label: string;
    description: string;
    documentType: string;
    isRequired: boolean;
  }>;

  // Invite tab data
  invitationMode: 'CLOSED' | 'OPEN';
  invitedSupplierIds: string[];
  isPublic: boolean;

  // Review tab data
  title: string;
  description: string;
  dueDate: string;
}

interface PublishSourcingFormProps {
  sourcingId: string;
  sourcingData: any;
  onBack: () => void;
}

export const PublishSourcingForm: React.FC<PublishSourcingFormProps> = ({
  sourcingId,
  sourcingData,
  onBack,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const formDataRef = useRef<any>({});

  // Callback to update form data from child components
  const updateFormData = (newData: any) => {
    formDataRef.current = { ...formDataRef.current, ...newData };
  };

  const steps = [
    {
      title: 'Configure',
      description: 'Select template and configure fields',
    },
    {
      title: 'Invite',
      description: 'Choose suppliers and invitation mode',
    },
    {
      title: 'Review & Publish',
      description: 'Review and publish to portal',
    },
  ];

  const handleNext = async () => {
    try {
      // Validate current step before proceeding
      // const currentTab = steps[currentStep].title.toLowerCase() as keyof PublishSourcingFormData;

      // For configure tab, validate required fields
      if (currentStep === 0) {
        const values = await form.validateFields(['templateId', 'sourcingType', 'biddingType']);
        if (!values.templateId || !values.sourcingType || !values.biddingType) {
          message.error('Please complete the configuration step');
          return;
        }
      }

      // For invite tab, validate supplier selection
      if (currentStep === 1) {
        const values = await form.validateFields(['invitationMode', 'invitedSupplierIds']);
        if (values.invitationMode === 'CLOSED' && (!values.invitedSupplierIds || values.invitedSupplierIds.length === 0)) {
          message.error('Please select at least one supplier for closed sourcing');
          return;
        }
      }

      console.log('PublishSourcingForm: Moving to next step, current form data:', form.getFieldsValue());
      if (currentStep < steps.length - 1) {
        // Save current form data before switching tabs
        const currentFormData = form.getFieldsValue();
        updateFormData(currentFormData);
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      console.error('Validation error:', error);
      message.error('Please complete all required fields before proceeding');
    }
  };

  const handlePrevious = () => {
    console.log('PublishSourcingForm: Moving to previous step, current form data:', form.getFieldsValue());
    if (currentStep > 0) {
      // Save current form data before switching tabs
      const currentFormData = form.getFieldsValue();
      updateFormData(currentFormData);
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePublish = async (values: PublishSourcingFormData) => {
    setLoading(true);
    try {
      // Prepare the input data for the supplier portal
      const publishInput = {
        title: values.title,
        sourcingType: values.sourcingType,
        biddingType: values.biddingType,
        description: values.description,
        dueDate: values.dueDate,
        templateId: values.templateId,
        isPublic: values.isPublic || false,
        invitedSupplierIds: values.invitedSupplierIds || [],
        documents: [], // TODO: Add document handling if needed
        fields: values.fields || [],
        fileRequirements: values.fileRequirements || [],
      };

      console.log('Publishing sourcing with data:', publishInput);

      // Call the GraphQL mutation to create sourcing in supplier portal
      const response = await gqlClient.mutation(SUPPLIER_PORTAL_PUBLISH_SOURCING_MUTATION, {
        input: publishInput,
        sourcingId: sourcingId // Pass the sourcing ID to update local record
      });

      if (response.data?.publishSupplierPortalSourcing) {
        message.success('Sourcing published to supplier portal successfully!');
        navigate(`/sourcings/show/${sourcingId}`);
      } else {
        throw new Error('Failed to publish sourcing');
      }
    } catch (error: any) {
      console.error('Publish error:', error);
      const errorMessage = error?.response?.errors?.[0]?.message || error?.message || 'Failed to publish sourcing. Please try again.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentTab = () => {
    // Use the saved form data state
    console.log('PublishSourcingForm: Rendering tab', currentStep, 'with formData:', formDataRef.current);
    const tabProps = {
      form,
      formData: formDataRef.current,
      sourcingData,
      updateFormData,
    };

    switch (currentStep) {
      case 0:
        return <ConfigureTab key="configure-tab" {...tabProps} isPublishing={true} />;
      case 1:
        return <InviteTab key="invite-tab" {...tabProps} />;
      case 2:
        return <ReviewTab key="review-tab" {...tabProps} onPublish={handlePublish} loading={loading} isPublishing={true} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{ marginBottom: '16px' }}
        >
          Back to Sourcing
        </Button>

        <Card>
          <div style={{ marginBottom: 24 }}>
            <Steps current={currentStep} size="small">
              {steps.map((step, index) => (
                <Step
                  key={index}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </Steps>
          </div>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              invitationMode: 'CLOSED',
              isPublic: false,
              invitedSupplierIds: [],
            }}
            preserve={true}
          >
            {renderCurrentTab()}
          </Form>

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                Previous
              </Button>
              {currentStep < steps.length - 1 ? (
                <Button type="primary" onClick={handleNext}>
                  Next
                </Button>
              ) : null}
            </Space>
          </div>
        </Card>
      </Space>
    </div>
  );
};